import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { UserService } from '../services/user.service';
import { PermissionService } from '../services/permission.service';
import { LoggerService } from '../services/logger.service';

/**
 * Pre-flight guard that runs before any other guards.
 * Ensures the user's account is fully set up before allowing access.
 *
 * Checks:
 * 1. User is authenticated (has valid token)
 * 2. Account is active
 * 3. Hotel user: has at least 1 hotel AND at least 1 access area
 * 4. Internal user: has a role assigned
 *
 * Fails → redirect to /setup-incomplete
 */
export const GlobalAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const userService = inject(UserService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  // Don't check setup for the setup-incomplete page itself (avoid redirect loop)
  if (state.url.includes('setup-incomplete')) {
    return true;
  }

  // AuthGuard already verifies token existence and expiry before this guard runs.
  // We only need to check user data here — no redundant isLoggedIn() call.
  const user = userService.getUser();

  // No user data yet (e.g. /me call still in flight) — allow through,
  // AuthGuard already verified the token is valid
  if (!user) {
    return true;
  }

  // Check if account is active
  if (user.is_active === false) {
    logger.warn('User account is inactive');
    userService.clearSessionLocally();
    return router.createUrlTree(['/account/login'], {
      queryParams: { reason: 'account_inactive' }
    });
  }

  // Check if setup is complete — pass the specific reason if not
  const setupReason = permissionService.getSetupIncompleteReason();
  if (setupReason) {
    logger.warn(`User setup incomplete (${setupReason}) — redirecting to setup-incomplete page`);
    return router.createUrlTree(['/setup-incomplete'], {
      queryParams: { reason: setupReason }
    });
  }

  return true;
};
